import { type PointerEvent, useEffect, useRef } from "react";

type MotionMediaProps = {
  src: string;
  alt: string;
  className?: string;
  caption?: string;
  eager?: boolean;
  speed?: number;
};

const MotionMedia = ({
  src,
  alt,
  className = "",
  caption,
  eager = false,
  speed = 34,
}: MotionMediaProps) => {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let frame = 0;
    const update = () => {
      const bounds = element.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      const elementCenter = bounds.top + bounds.height / 2;
      const progress = Math.max(-1, Math.min(1, (viewportCenter - elementCenter) / window.innerHeight));
      element.style.setProperty("--media-shift", `${progress * speed}px`);
      frame = 0;
    };
    const requestUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate, { passive: true });
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
    };
  }, [speed]);

  const handlePointerMove = (event: PointerEvent<HTMLElement>) => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - bounds.left) / bounds.width - 0.5;
    const y = (event.clientY - bounds.top) / bounds.height - 0.5;
    event.currentTarget.style.setProperty("--media-tilt-x", `${y * -4}deg`);
    event.currentTarget.style.setProperty("--media-tilt-y", `${x * 5}deg`);
    event.currentTarget.style.setProperty("--sheen-x", `${(x + 0.5) * 100}%`);
    event.currentTarget.style.setProperty("--sheen-y", `${(y + 0.5) * 100}%`);
  };

  const resetPointer = (event: PointerEvent<HTMLElement>) => {
    event.currentTarget.style.setProperty("--media-tilt-x", "0deg");
    event.currentTarget.style.setProperty("--media-tilt-y", "0deg");
  };

  return (
    <figure
      ref={ref}
      className={`motion-media${className ? ` ${className}` : ""}`}
      onPointerMove={handlePointerMove}
      onPointerLeave={resetPointer}
    >
      <div className="motion-media-frame">
        <img src={src} alt={alt} loading={eager ? "eager" : "lazy"} />
        <span className="motion-media-sheen" aria-hidden="true" />
      </div>
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
};

export default MotionMedia;
